import { FORM_DEFAULT_THEME, FORM_THEME_MAP, FORM_VARIANTS, resolveFormVariant } from '@shared/const/form/form.data'
import {
  DEFAULT_PHONE_MASK,
  DROPZONE_OPTIONS,
  FORM_ADD_PARAMS,
  FORM_FIELD_DEFAULT_SPAN,
  FORM_FIELD_TYPE,
  FORM_FIELD_VIEW,
  resolveDisableState,
  resolveType,
  resolveTypeField,
  resolveFieldValue,
  resolveFieldPatternAndMessage,
} from '@shared/const/form/form.field'

export function formNormalize({ formConfig = {}, formAgreements = [], formContext = {} } = {}) {
  const source = structuredClone(formConfig || {})

  const mode = resolveFormVariant(source)
  const isStepMode = mode === FORM_VARIANTS.STEPS

  const rawFields = Array.isArray(source.fields) ? source.fields : []

  const stepGroups = isStepMode ? normalizeStepGroups(rawFields, formContext) : []

  const fields = isStepMode ? stepGroups.map(stepGroup => stepGroup.fields) : normalizeFlatFields(rawFields, formContext)

  if (process.env.NODE_ENV !== 'development') {
    console.log('fields', fields)
  }

  appendAgreementFields({
    fields,
    formAgreements,
    isStepMode,
    formContext,
  })

  const steps = isStepMode ? normalizeSteps(stepGroups, fields) : []

  const hasFields = isStepMode ? fields.some(stepFields => stepFields.length > 0) : fields.length > 0

  return {
    ...source,

    id: source.id,
    title: source.title,
    description: source.description,
    action: source.action,
    type: source.type,
    redirect: source.redirect,
    placeId: source.placeId,
    nameEntity: source.nameEntity,
    entityId: source.entityId,

    theme: FORM_THEME_MAP[source.color] ?? FORM_DEFAULT_THEME,
    mode,
    isStepMode,

    fields,
    steps,

    hasFields,
    hasSteps: isStepMode && steps.length > 0,
  }
}

function normalizeFlatFields(rawFields, formContext) {
  if (!Array.isArray(rawFields)) {
    return []
  }

  return rawFields.filter(Boolean).map(field => normalizeFormField(field, formContext))
}

function normalizeStepGroups(rawSteps, formContext) {
  if (!Array.isArray(rawSteps)) {
    return []
  }

  return rawSteps
    .filter(Boolean)
    .map((rawStep, sourceIndex) => ({
      rawStep,
      sourceIndex,
      fields: normalizeStepFormFields(rawStep, formContext),
    }))
    .filter(stepGroup => Array.isArray(stepGroup.fields) && stepGroup.fields.length > 0)
}

function normalizeStepFormFields(rawStep, formContext) {
  const rawChildren = Array.isArray(rawStep?.children) ? rawStep.children : []

  return rawChildren.filter(Boolean).map(field => normalizeFormField(field, formContext))
}

function appendAgreementFields({ fields, formAgreements, isStepMode, formContext }) {
  if (!Array.isArray(formAgreements) || formAgreements.length === 0) {
    return
  }

  const agreementFields = createAgreements(formAgreements, formContext)

  if (!agreementFields.length) {
    return
  }

  if (!isStepMode) {
    fields.push(...agreementFields)
    return
  }

  if (!fields.length) {
    fields.push(agreementFields)
    return
  }

  fields[fields.length - 1].push(...agreementFields)
}

function createAgreements(formAgreements, formContext) {
  return formAgreements.filter(Boolean).map(agreement => ({
    ...normalizeFormField(agreement, formContext),
    isAgreement: true,
  }))
}

function normalizeSteps(stepGroups, fields) {
  if (!Array.isArray(stepGroups) || !Array.isArray(fields) || fields.length === 0) {
    return []
  }

  return fields.map((_, index) => {
    const stepGroup = stepGroups[index]
    const rawStep = stepGroup?.rawStep || {}
    const content = rawStep.content || {}

    return {
      id: content.id || `step-${index}`,
      title: content.title,
      description: content.description,
      index,
      sourceIndex: stepGroup?.sourceIndex ?? index,
    }
  })
}

function normalizeFormField(rawField, formContext = {}) {
  const content = getFieldContent(rawField)

  const [pattern, patternErrorText] = resolveFieldPatternAndMessage(content, formContext)

  const normalized = {
    ...content,

    name: content.name ?? content.id,
    pattern,
    patternErrorText,
    colSpan: content.width ?? FORM_FIELD_DEFAULT_SPAN,
    addParams: normalizeAddParams(content.addParams),
    raw: rawField,

    error: false,
    valid: true,
    errorText: null,
  }

  normalized.type = resolveType(normalized, formContext)
  normalized.typeField = resolveTypeField(normalized, formContext)
  normalized.value = resolveFieldValue(normalized, formContext)
  normalized.disabled = resolveDisableState(normalized, formContext)

  if (normalized.typeField === FORM_FIELD_TYPE.PHONE) {
    normalized.mask = formContext.region?.mask ?? DEFAULT_PHONE_MASK
  }

  if (normalized.typeField === FORM_FIELD_TYPE.VIEW_HIDDEN) {
    normalized.isHidden = true
  }

  if (hasLabelForId(normalized)) {
    normalized.labelForId = `${normalized.type}-${normalized.name}`
  }

  const options = resolveFieldOptions(normalized)

  if (options) {
    normalized.options = options
  }

  return normalized
}

function hasLabelForId(field) {
  return [
    FORM_FIELD_VIEW.TEXT,
    FORM_FIELD_VIEW.NUMBER,
    FORM_FIELD_VIEW.PHONE,
    FORM_FIELD_VIEW.TEXTAREA,
    FORM_FIELD_VIEW.DADATA_CITY,
    FORM_FIELD_VIEW.DADATA_ORG,
  ].includes(field.type)
}

function resolveFieldOptions(field) {
  switch (field.type) {
    case FORM_FIELD_VIEW.DROPZONE:
      return resolveDropzoneOptions(field)

    default:
      return null
  }
}

function resolveDropzoneOptions(field) {
  if (!field?.extensions) {
    return { ...DROPZONE_OPTIONS }
  }

  return {
    ...DROPZONE_OPTIONS,
    acceptedFiles: field.extensions
      .split(',')
      .map(item => item.trim())
      .filter(Boolean)
      .map(item => (item.startsWith('.') ? item : `.${item}`))
      .join(','),
  }
}

function normalizeAddParams(addParams) {
  if (!Array.isArray(addParams)) {
    return { [FORM_ADD_PARAMS.fieldProps]: {} }
  }

  const initialParams = { [FORM_ADD_PARAMS.fieldProps]: {} }

  return addParams.reduce((acc, param) => {
    if (!param?.code) {
      return acc
    }

    acc[param.code] = param.value
    return acc
  }, initialParams)
}

function getFieldContent(rawField) {
  return rawField?.content ? rawField.content : rawField ?? {}
}



// WEBPACK FOOTER //
// ./src/modules/shared/services/form/form.normalize.js